import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import get from 'lodash/get';
import { useFilterState } from '../components/cap';
import { getCartDetails } from '../aem-core-components/actions/cart';
import {
    SET_IS_CART_LOADING
} from '../aem-core-components/actions/constants';
import { useCartState } from '../contexts/cart';
import { ADDRESS_COMPONENTS, ADDRESS_COMPONENT_TYPE } from '../constants/cartConstants';
import {
    getClosestStores,
    getStores
} from '../components/checkoutv2/utils/editOrderDetailsFunctions';
import {
    determineCityComponent,
    isCheckoutPage
} from '../components/global/utils/commonUtils';
import {
    getAssetsFromPdpUrl,
    handleBranchSelectorCall,
    updateSelectedStoreDetailsForRoundTrip
} from '../components/global/utils/computeAddressUtil';
import { logError } from '../components/global/utils/logger';
import { getBSRListAPI, getPCfromLocations } from '../components/location/API/getStoreLocations';
import { STORAGE_CONFIG } from '../constants/storageConfig';
import { useCheckUser } from './useCheckUser';
import { useAtp } from '../components/cap/hooks/useATP';
import useUserData from './useUserData';
import useCheckLocationEmpty from './useCheckLocationEmpty';
import CART_DETAILS_QUERY from '../aem-core-components/queries/query_cart_details.graphql';
import { useAwaitQuery } from '../aem-core-components/utils/hooks';
import { updateContextBasedOnCompanyId } from '../components/search/Engine';
import { updateContextBasedOnCompanyId as plpUpdateContextBasedOnCompanyId } from '../components/search/PLPEngine';
import { ENV_CONFIG } from '../constants/envConfig';
import { SET_BSR_LIST } from '../components/cap/constants';

const useComputeLocation = () => {
    const [{ cart, howToGetYourOrderDetails, cartId }, dispatch] = useCartState();
    const [
        {
            viewCart,
            projectDetails,
            showUnavailableItems,
            startDate,
            endDate,
            selectedStoreDetails,
            searchRadiusForLocationCall
        },
        filterDispatch
    ] = useFilterState();
    const { pc } = viewCart;
    const [canadaRestrictStateError, setCanadaRestrictStateError] = useState('');
    const intl = useIntl();
    const userType = useCheckUser();
    const { handleATPCart } = useAtp();
    const [{ getAssets, updateGeography }] = useUserData();
    const validStateList = ENV_CONFIG.VALID_STATE_LIST;
    const { tier1SearchRadius, tier2SearchRadius } = searchRadiusForLocationCall || {};
    const cartDetailsQuery = useAwaitQuery(CART_DETAILS_QUERY);
    const { fetchLocationCoordinates, isRentalDetailsAvailable } = useCheckLocationEmpty();
    const [assets, setAssets] = useState([]);

    useEffect(() => {
        setCanadaRestrictStateError(canadaRestrictStateError);
    }, [canadaRestrictStateError]);

    useEffect(() => {
        setAssets(getAssets());
    }, [cart?.total_quantity]);
    const determineTypeOfAddress = component => {
        const types = get(component, 'types', []);
        if (types.includes(ADDRESS_COMPONENTS.SUBLOCALITY)) {
            return ADDRESS_COMPONENT_TYPE.SUBLOCALITY;
        }
        if (
            types.includes(ADDRESS_COMPONENTS.LOCALITY) ||
            types.includes(ADDRESS_COMPONENTS.AL3) ||
            types.includes(ADDRESS_COMPONENTS.AL2)
        ) {
            return ADDRESS_COMPONENT_TYPE.CITY;
        }
        if (types.includes(ADDRESS_COMPONENTS.AL1)) {
            return ADDRESS_COMPONENT_TYPE.STATE;
        }
        if (types.includes(ADDRESS_COMPONENTS.POSTAL)) {
            return ADDRESS_COMPONENT_TYPE.POSTAL_CODE;
        }
        if (types.includes(ADDRESS_COMPONENTS.STREET_NO)) {
            return ADDRESS_COMPONENT_TYPE.STREET;
        }
        if (types.includes(ADDRESS_COMPONENTS.COUNTRY)) {
            return ADDRESS_COMPONENT_TYPE.COUNTRY;
        }
        if (types.includes(ADDRESS_COMPONENTS.ROUTE)) {
            return ADDRESS_COMPONENT_TYPE.ROUTE;
        }
        if (types.includes(ADDRESS_COMPONENTS.NEIGHBORHOOD)) {
            return ADDRESS_COMPONENT_TYPE.NEIGHBORHOOD;
        }
    };

    const cityAndZipValidationAddressComponent = (details, companyID, countryShortName = null) => {
        const addressComponents = get(details, 'addressComponents', []);
        let isCityEmpty = true;
        let isZipEmpty = true;
        let isStateValid = true;
        let isStreetEmpty = true;

        const userCompanyId = companyID || localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.COMPANYID);

        addressComponents?.map(component => {
            const type = determineTypeOfAddress(component);
            switch (type) {
                case ADDRESS_COMPONENT_TYPE.STREET:
                case ADDRESS_COMPONENT_TYPE.NEIGHBORHOOD:
                case ADDRESS_COMPONENT_TYPE.ROUTE:
                    isStreetEmpty = false;
                    break;
                case ADDRESS_COMPONENT_TYPE.SUBLOCALITY:
                case ADDRESS_COMPONENT_TYPE.CITY:
                    isCityEmpty = false;
                    break;
                case ADDRESS_COMPONENT_TYPE.POSTAL_CODE:
                    isZipEmpty = false;
                    break;
                case ADDRESS_COMPONENT_TYPE.STATE:
                    if (countryShortName) {
                        isStateValid = countryShortName?.toLowerCase() === 'ca' ? !isValidState(component?.shortName) : false;
                    } else {
                        isStateValid = userCompanyId == 2 ? !isValidState(component?.shortName) : false;
                    }
                    break;
                default:
                    break;
            }
        });

        if (countryShortName) {
            // For MKTO, ignore street validation
            return !(isCityEmpty || isZipEmpty || isStateValid);
        } else {
            return !(isCityEmpty || isZipEmpty || isStateValid || isStreetEmpty);
        }
    };
    const extractAddressComponents = (details, isUpdateCountry = true, isMKTO = false) => {
        let computedAddress = {
            jobSiteAddr2: '',
            jobSiteCity: '',
            jobSiteState: '',
            jobSiteZip: '',
            country: isMKTO ? '' : parseInt(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.COMPANYID)) || 1
        };

        const addressComponents = get(details, 'addressComponents', []);
        let citySelected = false;

        addressComponents?.map(component => {
            const type = determineTypeOfAddress(component);
            switch (type) {
                case ADDRESS_COMPONENT_TYPE.CITY:
                    computedAddress.jobSiteCity = determineCityComponent(addressComponents);
                    citySelected = true;
                    break;
                case ADDRESS_COMPONENT_TYPE.STATE:
                    computedAddress.jobSiteState = component?.shortName;
                    break;
                case ADDRESS_COMPONENT_TYPE.POSTAL_CODE:
                    computedAddress.jobSiteZip = component?.longName;
                    break;
                case ADDRESS_COMPONENT_TYPE.AREA_L2:
                    computedAddress.jobSiteAddr2 = component?.longName;
                    break;
                case ADDRESS_COMPONENT_TYPE.SUBLOCALITY:
                    if (!citySelected) {
                        computedAddress.jobSiteCity = component?.longName;
                    }
                    break;
                case ADDRESS_COMPONENT_TYPE.COUNTRY:
                    if (!isMKTO) {
                        if (isUpdateCountry) {
                            localStorage.setItem('companyID', component?.shortName === 'CA' ? 2 : 1);
                        } else {
                            computedAddress.country = component?.shortName === 'CA' ? 2 : 1;
                        }
                    }
                    break;
                default:
                    break;
            }
        });

        return computedAddress;
    };


    const getBSRList = async ({ lat, long, companyID, updateInContext = true }) => {
        let localLat = '';
        let localLong = '';
        if (lat && long) {
            localLat = lat;
            localLong = long;
        } else {
            ({ localLat, localLong } = fetchLocationCoordinates());
        }
        try {
            if (localLat && localLong) {
                const { data, error } = await getBSRListAPI(localLat, localLong, companyID);
                if (error) {
                    logError(error, false, 'getBSRList', []);
                } else if (updateInContext) {
                    filterDispatch({ type: SET_BSR_LIST, bsrList: data || [] });
                }
                return { data: data, error: error };
            }
        } catch (error) {
            logError(error, false, 'getBSRList', []);
        }
    };

    const getLocationPCs = async (latitude, longitude, distance = tier1SearchRadius) => {
        try {
            const cidPcList = JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.CID_PC_LIST) || '[]');
            let locationPCObj = JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.LOCATIONPCOBJ)) || {};
            if (
                locationPCObj?.localLat == latitude &&
                locationPCObj?.localLong == longitude &&
                locationPCObj?.pc &&
                cidPcList?.length != 0
            ) {
                return {
                    pcObj: {
                        pcVal: locationPCObj?.pc,
                        pcLat: locationPCObj?.lat,
                        pcLong: locationPCObj?.long,
                        distance: locationPCObj?.distance,
                        specialtyTypes: locationPCObj?.specialtyTypes
                    },
                    isIdenticalPcList: true
                };
            } else {
                const {
                    pcObj,
                    pcList,
                    distance: pcDistance,
                    error
                } = await getPCfromLocations(latitude, longitude, distance, dispatch, showUnavailableItems);
                if (error) {
                    logError(error, false, 'getLocationPCs', [latitude, longitude, distance]);
                    return { error };
                } else {
                    return { pcObj, pcList, distance: pcDistance, error };
                }
            }
        } catch (error) {
            dispatch({ type: 'endLoading' });
            logError(error, false, 'getLocationPCs', [latitude, longitude, distance]);
            return { error };
        }
    };

    const getPC = async (
        localLat = '',
        localLong = '',
        localAssets = [],
        updateLocationInContext = true,
        pcPayload = {}
    ) => {
        try {
            let updatedAssets = assets;
            if (!cart && cartId) {
                const assetsFromStorage = JSON.parse(
                    sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.LOCAL_ASSETS_DATA) || '[]'
                );
                if (assetsFromStorage?.length > 0) {
                    updatedAssets = assetsFromStorage;
                } else {
                    let cartData = await getCartDetails({
                        cartDetailsQuery,
                        dispatch,
                        cartId,
                        filterDispatch,
                        handleATPCart,
                        isRentalDetailsAvailable: isRentalDetailsAvailable()
                    });
                    updatedAssets = getAssets(cartData);
                }
            }

            let pcVal = 0;
            let pcLat = '';
            let pcLong = '';
            let lat = '';
            let long = '';
            let localStartDate = pcPayload?.startDate || startDate;
            let isDelivery = pcPayload?.isDelivery || !viewCart?.isInStorePickup;
            let localEndDate = pcPayload?.endDate || endDate;
            let distance = '';
            let specialtyTypes = [];
            const assetsFromPdpUrl = getAssetsFromPdpUrl();
            let storeData = {};
            let overridePC = JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.OVERRIDEPC) || '{}');
            if (overridePC?.pc) {
                updateSelectedStoreDetailsForRoundTrip(overridePC);
                pcVal = overridePC?.pc;
                pcLat = overridePC?.latitude;
                pcLong = overridePC?.longitude;
                specialtyTypes = overridePC?.specialtyTypes;
                const contextKeyValues = [
                    {
                        key: 'nearbypcs',
                        val: []
                    }
                ];
                if (window.location.pathname.includes('/search')) {
                    updateContextBasedOnCompanyId([], contextKeyValues);
                } else {
                    plpUpdateContextBasedOnCompanyId(contextKeyValues);
                }
            } else {
                dispatch({ type: 'beginLoading' });
                if (localLat && localLong) {
                    lat = localLat;
                    long = localLong;
                } else {
                    const coords = fetchLocationCoordinates();
                    lat = coords?.localLat;
                    long = coords?.localLong;
                }
                if (localAssets?.length > 0) {
                    updatedAssets = localAssets;
                } else if (assets?.length > 0) {
                    updatedAssets = assets;
                } else if (assetsFromPdpUrl.length > 0) {
                    updatedAssets = assetsFromPdpUrl;
                }
                if (localStartDate && localEndDate && updatedAssets?.length > 0) {
                    if (updateLocationInContext) {
                        sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.SOURCEPCOBJ);
                    }
                    const currentStep = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.CHECKOUT_CURRENT_STEP);
                    ({ pcVal, pcLat, pcLong, distance, specialtyTypes, storeData } = await handleBranchSelectorCall(
                        pcPayload?.storeLat || lat,
                        pcPayload?.storeLong || long,
                        localStartDate,
                        localEndDate,
                        updatedAssets,
                        isDelivery,
                        pcPayload?.isLocationChanged || (isCheckoutPage() && currentStep == 1)
                    ));
                    if (!pcVal) {
                        dispatch({ type: 'endLoading' });
                        dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
                    }
                } else {
                    const { pcObj } = await getLocationPCs(lat, long, tier1SearchRadius);
                    ({ pcVal, pcLat, pcLong, distance, specialtyTypes } = pcObj);
                    dispatch({ type: 'endLoading' });
                    return { pcVal, pcLat, pcLong };
                }
            }
            dispatch({ type: 'endLoading' });
            if (updateLocationInContext) {
                updateGeography(pcVal, pcLat, pcLong, distance, specialtyTypes, lat, long, updatedAssets);
            }

            return { pcVal, pcLat, pcLong };
        } catch (e) {
            dispatch({ type: 'endLoading' });
        }
    };

    const isValidState = state => {
        if (validStateList?.includes(state)) {
            setCanadaRestrictStateError('');
            return true;
        } else {
            setCanadaRestrictStateError(intl.formatMessage({ id: 'error:canada-invalid-state' }));
            return false;
        }
    };

    const cityAndZipWithoutStreetValidation = (details, companyID) => {
        const addressComponents = get(details, 'addressComponents', []);
        let isCityEmpty = true;
        let isStateValid = true;
        const companyId = companyID || localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.COMPANYID) || 1;

        if (addressComponents?.length > 0) {
            addressComponents?.map(component => {
                const type = determineTypeOfAddress(component);
                switch (type) {
                    case ADDRESS_COMPONENT_TYPE.CITY: {
                        isCityEmpty = false;
                        break;
                    }
                    case ADDRESS_COMPONENT_TYPE.SUBLOCALITY: {
                        isCityEmpty = false;
                        break;
                    }
                    case ADDRESS_COMPONENT_TYPE.STATE: {
                        isStateValid = companyId == 2 ? !isValidState(component.shortName) : false;
                        break;
                    }
                    default:
                        break;
                }
            });
        }
        if (isCityEmpty || isStateValid) {
            return false;
        } else {
            return true;
        }
    };

    const storesCalls = async () => {
        dispatch({ type: 'beginLoading' });
        const viewCartFromStorage = localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.VIEWCART)
            ? JSON.parse(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.VIEWCART))
            : viewCart;
        getStores(
            viewCartFromStorage,
            startDate,
            endDate,
            projectDetails,
            userType,
            dispatch,
            assets,
            howToGetYourOrderDetails,
            false
        );
        getClosestStores(
            viewCartFromStorage,
            startDate,
            endDate,
            projectDetails,
            userType,
            dispatch,
            assets,
            howToGetYourOrderDetails
        );
    };

    const getZipFromLatLong = details => {
        let jobSiteZip = '';
        const addressComponents = get(details, 'addressComponents', []);
        if (addressComponents?.length > 0) {
            addressComponents?.map(component => {
                const type = determineTypeOfAddress(component);
                if (type == ADDRESS_COMPONENT_TYPE.POSTAL_CODE) {
                    jobSiteZip = component.longName;
                    return;
                }
            });
        }
        return jobSiteZip;
    };

    return {
        getPC,
        cityAndZipValidationAddressComponent,
        extractAddressComponents,
        storesCalls,
        cityAndZipWithoutStreetValidation,
        getZipFromLatLong,
        getLocationPCs,
        getBSRList
    };
};
export default useComputeLocation;
